import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const alertVariants = cva(
  // Premium treatment: the icon sits in a rounded tinted "chip" (the svg itself
  // gets a bg + padding), vertically centered with the text, over a whisper of
  // tone tint with a hairline tone border and the lit-slab `shadow-premium`.
  // Variants below set the tone (border, tint, chip bg, icon + text colour).
  'relative grid w-full items-center gap-y-1 rounded-lg border px-4 py-3 text-sm shadow-premium grid-cols-[0_1fr] has-[>svg]:grid-cols-[2rem_1fr] has-[>svg]:gap-x-3 [&>svg]:size-8 [&>svg]:shrink-0 [&>svg]:rounded-md [&>svg]:p-1.5',
  {
    variants: {
      variant: {
        default:
          'border-border bg-card text-card-foreground [&>svg]:bg-muted [&>svg]:text-muted-foreground',
        info: 'border-cobalt/25 bg-cobalt/[0.07] text-foreground [&>svg]:bg-cobalt/15 [&>svg]:text-cobalt *:data-[slot=alert-description]:text-foreground/90',
        success:
          'border-emerald/25 bg-emerald/[0.07] text-foreground [&>svg]:bg-emerald/15 [&>svg]:text-emerald *:data-[slot=alert-description]:text-foreground/90',
        warning:
          'border-amber/25 bg-amber/[0.07] text-foreground [&>svg]:bg-amber/15 [&>svg]:text-amber *:data-[slot=alert-description]:text-foreground/90',
        destructive:
          'border-destructive/25 bg-destructive/[0.07] text-foreground [&>svg]:bg-destructive/15 [&>svg]:text-destructive *:data-[slot=alert-description]:text-foreground/90',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight',
        className
      )}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      // Block layout (not grid) — grid was promoting every inline child
      // (`<span>`, plain text fragments around it) to its own row, which
      // wrecked sentences containing inline emphasis like
      // "Only <em>Ready</em> creatives…".
      className={cn(
        'text-muted-foreground col-start-2 text-sm leading-snug [&_p]:leading-relaxed',
        className
      )}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription };
